/// <reference types="@types/google.maps" />
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AutoTranslateText from '../../../components/AutoTranslateText';
import { createPortal } from 'react-dom';
import { PluginPreviewProps } from '../../../types/PluginInterface';
import {
  RentalConfig, RentalItem, AvailabilityRange,
  VACATION_AMENITIES, CATEGORY_LABELS, CATEGORY_ICONS, CustomBadge,
} from '../types';
import { useGoogleMapsApiKey } from '../../../hooks/useGoogleMapsApiKey';
import { api } from '../../../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import PayPalCheckoutButtons from '../../../components/PayPalCheckoutButtons';
import StripePaymentElementForm from '../../../components/StripePaymentElementForm';
import { ALL_COUNTRIES } from '../../../utils/countries';
import { loadGoogleMapsApi } from '../../../utils/googleMapsLoader';

// ── Helpers ────────────────────────────────────────────────────────────────
const isVideoUrl = (url: string) => /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(url.split('?')[0]);

const decodeHtml = (value: string) => {
  if (!value) return value;
  if (!value.includes('&lt;') && !value.includes('&gt;') && !value.includes('&amp;')) return value;
  if (typeof window === 'undefined') return value;
  const el = window.document.createElement('textarea');
  el.innerHTML = value;
  return el.value;
};

const getVideoEmbedUrl = (url: string): string | null => {
  try {
    const t = url.trim();
    if (t.includes('youtube.com')) {
      const id = new URL(t).searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1` : null;
    }
    if (t.includes('youtu.be/')) {
      const id = t.split('youtu.be/')[1]?.split(/[?&]/)[0];
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1` : null;
    }
    if (t.includes('vimeo.com/')) {
      const id = t.split('vimeo.com/')[1]?.split(/[?&]/)[0];
      return id ? `https://player.vimeo.com/video/${id}?autoplay=1&muted=1` : null;
    }
    return null;
  } catch { return null; }
};

const normalizeStripeKey = (v?: string | null) => {
  if (!v) return undefined;
  const t = v.trim();
  if (!t) return undefined;
  if (t.toLowerCase().startsWith('pk_')) return t;
  const p = t.split(':')[0].trim();
  return p.toLowerCase().startsWith('pk_') ? p : undefined;
};

const normalizePaypalKey = (v?: string | null) => {
  if (!v) return undefined;
  const t = v.trim();
  if (!t) return undefined;
  return t.split(':')[0].trim() || undefined;
};

// ── Prix dynamique ─────────────────────────────────────────────────────────
const getEffectivePriceForDay = (item: RentalItem, date: Date): number => {
  if (item.seasonalPrices?.length) {
    for (const sp of item.seasonalPrices) {
      const start = new Date(sp.startDate);
      const end = new Date(sp.endDate);
      if (date >= start && date <= end) return sp.pricePerDay;
    }
  }
  const dow = date.getDay();
  if ((dow === 5 || dow === 6) && item.weekendPricePerDay) return item.weekendPricePerDay;
  return item.pricePerDay;
};

interface PriceBreakdown {
  nights: number;
  nightsTotal: number;
  cleaningFee: number;
  touristTax: number;
  total: number;
}

const calcPrice = (item: RentalItem, startStr: string, endStr: string, guests: number, cfg: RentalConfig): PriceBreakdown => {
  const zero = { nights: 0, nightsTotal: 0, cleaningFee: 0, touristTax: 0, total: 0 };
  if (!startStr || !endStr) return zero;
  const s = new Date(startStr); const e = new Date(endStr);
  const nights = Math.ceil((e.getTime() - s.getTime()) / 86400000);
  if (nights <= 0) return zero;

  let nightsTotal = 0;
  const cursor = new Date(s);
  for (let i = 0; i < nights; i++) {
    nightsTotal += getEffectivePriceForDay(item, cursor);
    cursor.setDate(cursor.getDate() + 1);
  }

  // Long-stay discounts
  if (item.longStayDiscounts?.length) {
    const sorted = [...item.longStayDiscounts].sort((a, b) => b.minNights - a.minNights);
    for (const d of sorted) {
      if (nights >= d.minNights) { nightsTotal *= (1 - d.discountPercent / 100); break; }
    }
  }
  // Last-minute discount
  if (item.lastMinuteDiscountPercent) {
    const today = new Date(); today.setHours(0,0,0,0);
    const diff = Math.ceil((s.getTime() - today.getTime()) / 86400000);
    if (diff <= 3) nightsTotal *= (1 - item.lastMinuteDiscountPercent / 100);
  }

  const cleaningFee = item.cleaningFee || 0;
  const touristTax = Math.round((cfg.touristTaxPerPersonPerNight || 0) * guests * nights * 100) / 100;
  const total = Math.round((nightsTotal + cleaningFee + touristTax) * 100) / 100;
  return { nights, nightsTotal: Math.round(nightsTotal * 100) / 100, cleaningFee, touristTax, total };
};

// ── DateRangePicker — calendrier interactif de sélection arrivée/départ ───────
const MONTH_NAMES = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const toDateKey = (y: number, m: number, d: number) => `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

const DateRangePicker: React.FC<{
  bookings: AvailabilityRange[];
  startDate: string;
  endDate: string;
  onStartChange: (v: string) => void;
  onEndChange: (v: string) => void;
  primaryColor: string;
  minNights?: number;
  maxNights?: number;
}> = ({ bookings, startDate, endDate, onStartChange, onEndChange, primaryColor, minNights, maxNights }) => {
  const today = new Date(); today.setHours(0,0,0,0);
  const [viewDate, setViewDate] = useState(() => {
    if (startDate) return new Date(startDate + 'T12:00:00');
    const d = new Date(); d.setDate(1); return d;
  });
  const [hoverDate, setHoverDate] = useState<string>('');
  const [selecting, setSelecting] = useState<'start' | 'end'>('start');

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay();
  const blanks = Array.from({ length: firstDow === 0 ? 6 : firstDow - 1 });

  const isBlocked = (key: string) => {
    const d = new Date(key + 'T12:00:00');
    return bookings.some(b => {
      const s = new Date(b.startDate); s.setHours(0,0,0,0);
      const e = new Date(b.endDate); e.setHours(23,59,59,999);
      return d >= s && d <= e;
    });
  };

  const isPast = (key: string) => new Date(key + 'T12:00:00') < today;

  const isInRange = (key: string) => {
    const s = startDate; const e = endDate || hoverDate;
    if (!s || !e) return false;
    const [lo, hi] = s <= e ? [s, e] : [e, s];
    return key > lo && key < hi;
  };

  const isStart = (key: string) => key === startDate;
  const isEnd   = (key: string) => key === endDate;

  const handleDayClick = (key: string) => {
    if (isBlocked(key) || isPast(key)) return;
    if (selecting === 'start' || !startDate) {
      onStartChange(key); onEndChange('');
      setSelecting('end');
    } else {
      if (key < startDate) { onStartChange(key); onEndChange(''); setSelecting('end'); return; }
      // Check no blocked day in range
      const s = new Date(startDate + 'T12:00:00'); const e = new Date(key + 'T12:00:00');
      const blocked = bookings.some(b => {
        const bs = new Date(b.startDate); bs.setHours(0,0,0,0);
        const be = new Date(b.endDate); be.setHours(23,59,59,999);
        return bs <= e && be >= s;
      });
      if (blocked) { onStartChange(key); onEndChange(''); setSelecting('end'); return; }
      onEndChange(key);
      setSelecting('start');
    }
  };

  const formatDisplay = (v: string) => v ? new Date(v + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const nightsCount = startDate && endDate
    ? Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000)
    : 0;

  return (
    <div className="border rounded bg-white" style={{ userSelect: 'none' }} onClick={e => e.stopPropagation()}>
      {/* Récap dates sélectionnées */}
      <div className="d-flex border-bottom" style={{ fontSize: '0.8rem' }}>
        <div
          className="flex-fill p-2 text-center"
          style={{ cursor: 'pointer', backgroundColor: selecting === 'start' ? primaryColor + '22' : 'transparent', borderRight: '1px solid #e2e8f0' }}
          onClick={e => { e.stopPropagation(); setSelecting('start'); }}
        >
          <div className="text-muted" style={{ fontSize: '0.7rem' }}>ARRIVÉE</div>
          <div className="fw-semibold" style={{ color: startDate ? primaryColor : '#aaa' }}>{formatDisplay(startDate)}</div>
        </div>
        <div
          className="flex-fill p-2 text-center"
          style={{ cursor: 'pointer', backgroundColor: selecting === 'end' ? primaryColor + '22' : 'transparent' }}
          onClick={e => { e.stopPropagation(); if (startDate) setSelecting('end'); }}
        >
          <div className="text-muted" style={{ fontSize: '0.7rem' }}>DÉPART</div>
          <div className="fw-semibold" style={{ color: endDate ? primaryColor : '#aaa' }}>{formatDisplay(endDate)}</div>
        </div>
      </div>

      {/* Navigation mois */}
      <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
        <button type="button" className="btn btn-sm btn-link p-1 text-decoration-none"
          onClick={e => { e.stopPropagation(); setViewDate(new Date(year, month - 1, 1)); }}>‹</button>
        <span className="fw-semibold small">{MONTH_NAMES[month]} {year}</span>
        <button type="button" className="btn btn-sm btn-link p-1 text-decoration-none"
          onClick={e => { e.stopPropagation(); setViewDate(new Date(year, month + 1, 1)); }}>›</button>
      </div>

      {/* Jours */}
      <div className="p-1">
        <div className="d-flex mb-1">
          {["L","M","M","J","V","S","D"].map((d, i) => (
            <div key={i} className="text-center text-muted" style={{ width: '14.28%', fontSize: '0.65rem' }}>{d}</div>
          ))}
        </div>
        <div className="d-flex flex-wrap">
          {blanks.map((_, i) => <div key={`b${i}`} style={{ width: '14.28%', height: 32 }} />)}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
            const key = toDateKey(year, month, day);
            const blocked = isBlocked(key);
            const past = isPast(key);
            const inRange = isInRange(key);
            const isS = isStart(key);
            const isE = isEnd(key);
            const isHov = !endDate && hoverDate === key && startDate && key > startDate;
            const disabled = blocked || past;

            let bg = 'transparent', color = '#333', radius = '50%';
            if (isS || isE) { bg = primaryColor; color = '#fff'; }
            else if (inRange || isHov) { bg = primaryColor + '33'; color = primaryColor; radius = '0'; }
            if (blocked) { bg = '#fee2e2'; color = '#dc2626'; }
            if (past) { color = '#ccc'; }

            return (
              <div key={day} style={{ width: '14.28%', height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div
                  style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: radius, backgroundColor: bg, color, fontSize: '0.75rem', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: (isS || isE) ? 700 : 400, textDecoration: blocked ? 'line-through' : 'none', transition: 'background 0.1s' }}
                  title={blocked ? 'Indisponible' : past ? 'Passé' : key}
                  onClick={e => { e.stopPropagation(); if (!disabled) handleDayClick(key); }}
                  onMouseEnter={() => { if (!disabled && startDate && !endDate) setHoverDate(key); }}
                  onMouseLeave={() => setHoverDate('')}
                >{day}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Légende + infos */}
      <div className="border-top px-2 py-1 d-flex justify-content-between align-items-center flex-wrap gap-1" style={{ fontSize: '0.7rem' }}>
        <div className="d-flex gap-2 text-muted">
          <span><span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', backgroundColor:'#fee2e2', border:'1px solid #dc2626', marginRight:3 }}></span>Occupé</span>
          {startDate && endDate && <span style={{ color: primaryColor, fontWeight: 600 }}>✓ {nightsCount} nuit{nightsCount > 1 ? 's' : ''}</span>}
        </div>
        <div className="d-flex gap-1 text-muted">
          {minNights && <span>min. {minNights}n</span>}
          {maxNights && <span>max. {maxNights}n</span>}
        </div>
      </div>
    </div>
  );
};

// ── Badge chip ─────────────────────────────────────────────────────────────
const BADGE_CONF: Record<string, { label: string; bg: string; color: string }> = {
  'new': { label: '🆕 Nouveauté', bg: '#3b82f6', color: '#fff' },
  'coup-de-coeur': { label: '❤️ Coup de cœur', bg: '#ec4899', color: '#fff' },
  'derniere-minute': { label: '⚡ Dernière minute', bg: '#f59e0b', color: '#fff' },
  'exclusivite': { label: '🌟 Exclusivité', bg: '#8b5cf6', color: '#fff' },
};

const getBadgeConf = (badgeId: string | undefined, customBadges?: CustomBadge[]): { label: string; bg: string; color: string } | null => {
  if (!badgeId) return null;
  if (customBadges?.length) {
    const custom = customBadges.find(b => b.id === badgeId);
    if (custom) return custom;
  }
  return BADGE_CONF[badgeId] || null;
};

// ── Main Component ─────────────────────────────────────────────────────────
export const RentalPreview: React.FC<PluginPreviewProps> = ({ config }) => {
  const cfg = config as RentalConfig;

  // ── Search / filter state ────────────────────────────────────────────────
  const [searchCheckIn, setSearchCheckIn] = useState('');
  const [searchCheckOut, setSearchCheckOut] = useState('');
  const [searchGuests, setSearchGuests] = useState(1);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterAmenities, setFilterAmenities] = useState<string[]>([]);
  const [filterMaxPrice, setFilterMaxPrice] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'price-asc' | 'price-desc' | 'none'>('none');

  // ── Modal state ──────────────────────────────────────────────────────────
  const [activeItem, setActiveItem] = useState<RentalItem | null>(null);
  const [modalMediaIdx, setModalMediaIdx] = useState(0);

  // ── Booking state ────────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [guestsCount, setGuestsCount] = useState(1);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', postalCode: '', country: 'France'
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'success' | 'error' | 'cancelled'>('idle');
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [localBookings, setLocalBookings] = useState<Record<string, AvailabilityRange[]>>({});
  const [remoteBookings, setRemoteBookings] = useState<Record<string, AvailabilityRange[]>>({});
  const [merchantKeys, setMerchantKeys] = useState<{ stripePublicKey?: string; paypalClientId?: string }>({});

  const primaryColor = cfg.primaryColor || '#e63946';
  const bgStyle = cfg.backgroundColor === 'transparent' ? 'transparent' : (cfg.backgroundColor || '#ffffff');
  const textColor = (cfg as any).textColor || '#1e293b';
  const btnBg = cfg.itemButtonBackgroundColor || primaryColor;
  const btnTextColor = cfg.itemButtonTextColor || '#ffffff';

  const resolveOwnerId = (c: any) => {
    for (const k of ['userId', 'ownerId', 'createdBy', 'created_by', 'authorId']) {
      const v = c?.[k];
      if (v != null) { const s = String(v).trim(); if (s && s !== 'undefined' && s !== 'null') return s; }
    }
    return '';
  };
  const ownerId = resolveOwnerId(cfg);

  // ── Price breakdown (reactive) ───────────────────────────────────────────
  const priceBreakdown = useMemo(() => {
    if (!activeItem || !startDate || !endDate) return null;
    return calcPrice(activeItem, startDate, endDate, guestsCount, cfg);
  }, [activeItem, startDate, endDate, guestsCount, cfg.touristTaxPerPersonPerNight]);

  const totalPrice = priceBreakdown?.total || 0;

  // ── Merchant keys ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showCheckout || !ownerId) return;
    let cancelled = false;
    api.get<any>(`/payment-keys/public/${ownerId}`)
      .then(keys => {
        if (!cancelled) setMerchantKeys({ stripePublicKey: normalizeStripeKey(keys.stripePublicKey), paypalClientId: normalizePaypalKey(keys.paypalClientId) });
      })
      .catch(() => { if (!cancelled) setMerchantKeys({}); });
    return () => { cancelled = true; };
  }, [ownerId, showCheckout]);

  const stripePromise = useMemo(
    () => merchantKeys.stripePublicKey ? loadStripe(merchantKeys.stripePublicKey) : null,
    [merchantKeys.stripePublicKey]
  );
  const stripeElementsOptions = stripeClientSecret ? { clientSecret: stripeClientSecret, appearance: { theme: 'stripe' as const } } : undefined;

  useEffect(() => { if (paymentMethod !== 'stripe' && stripeClientSecret) setStripeClientSecret(null); }, [paymentMethod]);
  useEffect(() => { if (stripeClientSecret) setStripeClientSecret(null); }, [startDate, endDate, activeItem?.id]);

  // ── Remote reservations ──────────────────────────────────────────────────
  useEffect(() => {
    if (!activeItem || !ownerId) return;
    let cancelled = false;
    api.get<any>(`/rental-reservations/public/owner/${ownerId}?rentalItemId=${encodeURIComponent(activeItem.id)}`)
      .then(response => {
        if (cancelled) return;
        const normalized = (response?.reservations || []).map((r: any) => ({
          startDate: r.startDate, endDate: r.endDate, type: 'booking' as const,
          reason: r.status && r.status !== 'paid' ? 'Réservé (en attente)' : 'Réservé'
        }));
        setRemoteBookings(prev => ({ ...prev, [activeItem.id]: normalized }));
      })
      .catch(() => setRemoteBookings(prev => ({ ...prev, [activeItem.id]: [] })));
    return () => { cancelled = true; };
  }, [activeItem?.id, ownerId]);

  // ── Google Maps ──────────────────────────────────────────────────────────
  const { apiKey: googleMapsKey } = useGoogleMapsApiKey();
  useEffect(() => {
    if (!activeItem || showCheckout || !googleMapsKey || !activeItem.address) return;
    let cancelled = false;
    loadGoogleMapsApi(googleMapsKey).then(() => {
      if (cancelled || !window.google?.maps) return;
      setTimeout(() => {
        if (cancelled) return;
        const el = document.getElementById(`modal-map-${activeItem.id}`);
        if (el) {
          new window.google.maps.Geocoder().geocode({ address: activeItem.address }, (results: any, status: any) => {
            if (status === 'OK' && results?.[0]) {
              const map = new window.google.maps.Map(el, { center: results[0].geometry.location, zoom: 15 });
              new window.google.maps.Marker({ map, position: results[0].geometry.location });
            }
          });
        }
      }, 300);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeItem?.id, googleMapsKey, showCheckout]);

  // ── Body scroll lock ─────────────────────────────────────────────────────
  useEffect(() => {
    if (activeItem) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }
    document.body.style.overflow = '';
  }, [activeItem]);

  // ── Booking helpers ──────────────────────────────────────────────────────
  const getMergedBookings = useCallback((item: RentalItem | null) => {
    if (!item) return [];
    return [...(item.availability || []), ...(remoteBookings[item.id] || []), ...(localBookings[item.id] || [])];
  }, [remoteBookings, localBookings]);

  const parseDateOnly = (v?: string | null) => {
    if (!v) return null;
    const [y, m, d] = v.split('T')[0].split('-').map(Number);
    return new Date(y, m - 1, d, 12);
  };

  const isDateBlocked = (v: string, bookings: AvailabilityRange[]) => {
    const d = parseDateOnly(v);
    if (!d) return false;
    return bookings.some(b => {
      const s = parseDateOnly(b.startDate); const e = parseDateOnly(b.endDate);
      return s && e && d >= s && d <= e;
    });
  };

  const isRangeOverlapping = (s: Date, e: Date, bookings: AvailabilityRange[]) =>
    bookings.some(b => {
      const bs = parseDateOnly(b.startDate); const be = parseDateOnly(b.endDate);
      return bs && be && s <= be && e >= bs;
    });

  const isFormValid = () => Object.values(customerInfo).every(v => v.trim() !== '');

  // ── Checkout flow ────────────────────────────────────────────────────────
  const handleProceedToCheckout = () => {
    setPaymentStatus('idle');
    if (!activeItem || !startDate || !endDate) return;
    const s = parseDateOnly(startDate); const e = parseDateOnly(endDate);
    if (!s || !e || s >= e) { alert("Dates invalides."); return; }
    const rules = activeItem.rules;
    const nights = priceBreakdown?.nights || 0;
    if (rules?.minNights && nights < rules.minNights) { alert(`Durée minimum : ${rules.minNights} nuits.`); return; }
    if (rules?.maxNights && nights > rules.maxNights) { alert(`Durée maximum : ${rules.maxNights} nuits.`); return; }
    if (isRangeOverlapping(s, e, getMergedBookings(activeItem))) { alert("❌ Ces dates sont déjà réservées ou indisponibles."); return; }
    setShowCheckout(true);
  };

  const finalizeBooking = async (method: string, transactionDetails?: any) => {
    if (!activeItem) return;
    setIsProcessing(true); setPaymentStatus('idle');
    try {
      const newBooking: AvailabilityRange = {
        startDate, endDate, type: 'booking',
        reason: `Réservé par ${customerInfo.firstName} ${customerInfo.lastName} (${method})`,
        bookingId: transactionDetails?.id ? String(transactionDetails.id) : undefined,
        bookingDetails: { ...customerInfo, paymentMethod: method, totalPrice, currency: cfg.currency, transactionId: transactionDetails?.id ? String(transactionDetails.id) : undefined, pluginInstanceId: cfg.id, itemName: activeItem.name, createdAt: new Date().toISOString(), guestsCount }
      };

      if (cfg.userId) {
        try {
          const resp = await api.post<any>('/rental-reservations', {
            ownerId: Number(ownerId), rentalItemId: activeItem.id, startDate, endDate, totalPrice,
            currency: cfg.currency, paymentMethod: method,
            paymentStatus: method === 'manual' ? 'pending' : 'paid',
            transactionId: transactionDetails?.id ? String(transactionDetails.id) : undefined,
            reason: newBooking.reason, bookingDetails: newBooking.bookingDetails
          });
          if (resp?.id) newBooking.bookingId = String(resp.id);
        } catch (e) { console.error('Erreur persistance réservation:', e); }
      }

      setLocalBookings(prev => ({ ...prev, [activeItem.id]: [...(prev[activeItem.id] || []), newBooking] }));
      setPaymentStatus('success');
      const modeLabel = cfg.bookingMode === 'request' ? 'Votre demande a été envoyée. Le propriétaire vous contactera sous 24h.' : `Réservation confirmée. Un email récapitulatif a été envoyé.`;
      setPaymentMessage(modeLabel);
      setStartDate(''); setEndDate('');
      setTimeout(() => {
        setShowCheckout(false); setActiveItem(null);
        setCustomerInfo({ firstName: '', lastName: '', email: '', phone: '', address: '', city: '', postalCode: '', country: 'France' });
        setPaymentStatus('idle');
      }, 3000);
    } catch (err: any) {
      setPaymentStatus('error');
      setPaymentMessage(err.message || "Erreur lors de la réservation.");
    } finally { setIsProcessing(false); }
  };

  const handlePaymentSuccess = (details: any) => {
    const method = details?.method || 'paypal';
    if (method === 'paypal') {
      const status = String(details?.status || '').toUpperCase();
      if (status && !['COMPLETED', 'APPROVED'].includes(status)) {
        setPaymentStatus('error'); setPaymentMessage(`Paiement PayPal non validé (statut: ${status}).`); return;
      }
    }
    finalizeBooking(method, details);
  };
  const handlePaymentError = (_err: any) => {
    setIsProcessing(false);
    setPaymentStatus('error');
    setPaymentMessage("Le paiement n'a pas pu aboutir. Veuillez réessayer.");
  };
  const handlePaymentCancel = () => {
    setIsProcessing(false);
    setPaymentStatus('cancelled');
    setPaymentMessage("Paiement annulé. Aucune somme n'a été débitée.");
  };

  const createStripeIntent = async () => {
    if (!stripePromise || !ownerId || !activeItem || !isFormValid()) {
      setPaymentStatus('error'); setPaymentMessage(!isFormValid() ? "Veuillez compléter toutes vos informations." : "Aucune clé Stripe ou vendeur introuvable."); return;
    }
    setStripeBusy(true);
    try {
      const resp: any = await api.post('/rental-reservations/stripe-intent', {
        componentId: cfg.id, rentalItemId: activeItem.id, ownerId, startDate, endDate, totalPrice,
        currency: cfg.currency || 'EUR', customerInfo, bookingDetails: { itemName: activeItem.name }
      });
      if (!resp?.clientSecret) throw new Error(resp?.error || 'Client secret manquant.');
      setStripeClientSecret(resp.clientSecret);
    } catch (err: any) {
      setPaymentStatus('error'); setPaymentMessage(err?.message || 'Erreur Stripe.');
    } finally { setStripeBusy(false); }
  };

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    if (!ownerId) { setPaymentStatus('error'); setPaymentMessage('Vendeur introuvable.'); return; }
    setIsProcessing(true);
    try {
      const resp: any = await api.post('/rental-reservations/stripe-confirm-intent', { ownerId, paymentIntentId });
      if (!resp?.success) { setPaymentStatus('error'); setPaymentMessage(resp?.error || 'Paiement non confirmé.'); return; }
      const reservation = resp?.reservation;
      if (reservation?.rentalItemId) {
        setLocalBookings(prev => ({ ...prev, [reservation.rentalItemId]: [...(prev[reservation.rentalItemId] || []), { startDate: reservation.startDate, endDate: reservation.endDate, type: 'booking', reason: 'Réservé', bookingId: String(reservation.id), bookingDetails: reservation.bookingDetails }] }));
      }
      setPaymentStatus('success'); setPaymentMessage('Paiement confirmé. Votre réservation est validée.');
      setStripeClientSecret(null);
      setTimeout(() => { setShowCheckout(false); setActiveItem(null); setCustomerInfo({ firstName: '', lastName: '', email: '', phone: '', address: '', city: '', postalCode: '', country: 'France' }); setPaymentStatus('idle'); }, 3000);
    } catch (err: any) { setPaymentStatus('error'); setPaymentMessage(err?.message || 'Erreur de confirmation Stripe.'); }
    finally { setIsProcessing(false); }
  };

  // ── Filtered + sorted items ───────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    let items = cfg.items || [];

    if (filterCategory) items = items.filter(i => i.category === filterCategory);
    if (filterAmenities.length) items = items.filter(i => filterAmenities.every(a => (i.amenities || []).includes(a)));
    if (filterMaxPrice) items = items.filter(i => i.pricePerDay <= Number(filterMaxPrice));

    if (searchGuests > 1) items = items.filter(i => !i.capacity || i.capacity.guests >= searchGuests);

    if (searchCheckIn && searchCheckOut) {
      items = items.filter(i => {
        const bookings = getMergedBookings(i);
        const s = parseDateOnly(searchCheckIn); const e = parseDateOnly(searchCheckOut);
        if (!s || !e) return true;
        return !isRangeOverlapping(s, e, bookings);
      });
    }

    if (sortBy === 'price-asc') items = [...items].sort((a, b) => a.pricePerDay - b.pricePerDay);
    if (sortBy === 'price-desc') items = [...items].sort((a, b) => b.pricePerDay - a.pricePerDay);

    return items;
  }, [cfg.items, filterCategory, filterAmenities, filterMaxPrice, searchGuests, searchCheckIn, searchCheckOut, sortBy, remoteBookings, localBookings]);

  const openDetails = (item: RentalItem, e?: React.MouseEvent) => {
    e?.preventDefault(); e?.stopPropagation();
    setActiveItem(item); setModalMediaIdx(0);
    setStartDate(searchCheckIn); setEndDate(searchCheckOut);
    setGuestsCount(searchGuests || 1);
    setShowCheckout(false); setPaymentStatus('idle');
  };

  const handleCloseModal = () => { setShowCheckout(false); setActiveItem(null); };

  const handleStartDateChange = (v: string) => {
    if (!activeItem) { setStartDate(v); return; }
    if (isDateBlocked(v, getMergedBookings(activeItem))) { setStartDateError('Cette date est déjà réservée.'); return; }
    setStartDateError(null); setStartDate(v);
  };

  const handleEndDateChange = (v: string) => {
    if (!activeItem) { setEndDate(v); return; }
    if (isDateBlocked(v, getMergedBookings(activeItem))) { setEndDateError('Cette date est déjà réservée.'); return; }
    setEndDateError(null); setEndDate(v);
  };

  if (!cfg || !cfg.items) return <div className="alert alert-secondary"><AutoTranslateText sourceLang="fr">Module non configuré.</AutoTranslateText></div>;

  const activeMediaItems = useMemo(() => {
    if (!activeItem) return [];
    return activeItem.videoUrl ? [...(activeItem.images || []), activeItem.videoUrl] : (activeItem.images || []);
  }, [activeItem]);

  // ── Title style ───────────────────────────────────────────────────────────
  const getStyle = (prefix: string, defaultColor?: string, defaultSize?: string): React.CSSProperties => {
    const c = cfg as any;
    return {
      fontFamily: c[`${prefix}FontFamily`] !== 'inherit' ? c[`${prefix}FontFamily`] : undefined,
      fontSize: c[`${prefix}FontSize`] || defaultSize,
      color: c[`${prefix}Color`] || defaultColor,
      fontWeight: c[`${prefix}Bold`] ? 'bold' : 'normal',
      fontStyle: c[`${prefix}Italic`] ? 'italic' : 'normal',
      textDecoration: c[`${prefix}Underline`] ? 'underline' : 'none',
    };
  };

  return (
    <div style={{ backgroundColor: bgStyle, color: textColor }}>
      <style>{`
        .vacation-card { transition: transform 0.2s, box-shadow 0.2s; }
        .vacation-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.15) !important; }
        .vacation-card-img { height: 220px; overflow: hidden; }
        @media (max-width: 576px) { .vacation-card-img { height: 180px; } }
        .amenity-chip { font-size: 0.7rem; padding: 2px 8px; border-radius: 20px; background: #f1f5f9; color: #475569; display: inline-flex; align-items: center; gap: 3px; }
        .rental-checkout-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
        @media (min-width: 992px) { .rental-checkout-grid { grid-template-columns: 1fr 1fr; } }
      `}</style>

      {/* ── Header ── */}
      <div className="container-fluid px-3 px-md-4 pt-4">
        <h2 className="text-center mb-2" style={getStyle('title', '#212529', '2rem')}>{cfg.title}</h2>
        {cfg.description && (
          <div className="text-center mb-4" style={getStyle('description', '#6c757d', '1rem')} dangerouslySetInnerHTML={{ __html: decodeHtml(cfg.description) }} />
        )}

        {/* ── Barre de recherche ── */}
        {cfg.showSearchBar !== false && <div className="card shadow-sm mb-3 border-0" style={{ borderRadius: 12 }}>
          <div className="card-body py-3">
            <div className="row g-2">
              <div className="col-6 col-md-3">
                <label className="form-label small fw-semibold mb-1">📅 <AutoTranslateText sourceLang="fr">Arrivée</AutoTranslateText></label>
                <input type="date" className="form-control" style={{ height: 38 }} value={searchCheckIn} onChange={e => setSearchCheckIn(e.target.value)} />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small fw-semibold mb-1">📅 <AutoTranslateText sourceLang="fr">Départ</AutoTranslateText></label>
                <input type="date" className="form-control" style={{ height: 38 }} value={searchCheckOut} onChange={e => setSearchCheckOut(e.target.value)} />
              </div>
              <div className="col-4 col-md-2">
                <label className="form-label small fw-semibold mb-1">👥 <AutoTranslateText sourceLang="fr">Voyageurs</AutoTranslateText></label>
                <input type="number" className="form-control" style={{ height: 38 }} min={1} max={20} value={searchGuests} onChange={e => setSearchGuests(parseInt(e.target.value) || 1)} />
              </div>
              <div className="col-4 col-md-2">
                <label className="form-label small fw-semibold mb-1">🔀 <AutoTranslateText sourceLang="fr">Trier par</AutoTranslateText></label>
                <select className="form-select" style={{ height: 38 }} value={sortBy} onChange={e => setSortBy(e.target.value as any)}>
                  <option value="none">Pertinence</option>
                  <option value="price-asc">Prix ↑</option>
                  <option value="price-desc">Prix ↓</option>
                </select>
              </div>
              <div className="col-4 col-md-2 d-flex align-items-end">
                <button className="btn w-100" style={{ backgroundColor: primaryColor, color: '#fff', height: 38 }} onClick={() => setShowFilters(v => !v)}>
                  🔍 {showFilters ? 'Moins' : 'Filtres'}
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="row g-2 mt-2 pt-2 border-top">
                <div className="col-md-3">
                  <label className="form-label small">Type de bien</label>
                  <select className="form-select form-select-sm" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                    <option value="">Tous</option>
                    {['villa','maison','appartement','gite','chalet','studio','glamping','camping'].map(c => (
                      <option key={c} value={c}>{CATEGORY_ICONS[c]} {CATEGORY_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Prix max / nuit ({cfg.currency})</label>
                  <input type="number" className="form-control form-control-sm" placeholder="Ex: 300" value={filterMaxPrice} onChange={e => setFilterMaxPrice(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Équipements requis</label>
                  <div className="d-flex flex-wrap gap-1">
                    {VACATION_AMENITIES.slice(0, 8).map(a => (
                      <span
                        key={a.id}
                        className="badge rounded-pill"
                        style={{ cursor: 'pointer', backgroundColor: filterAmenities.includes(a.id) ? primaryColor : '#e2e8f0', color: filterAmenities.includes(a.id) ? '#fff' : '#475569', fontSize: '0.75rem', padding: '4px 10px' }}
                        onClick={() => setFilterAmenities(prev => prev.includes(a.id) ? prev.filter(x => x !== a.id) : [...prev, a.id])}
                      >{a.icon} {a.label}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>}

        {/* ── Résultats ── */}
        {filteredItems.length === 0 && (
          <div className="text-center py-5 text-muted">
            <div style={{ fontSize: 48 }}>🏖️</div>
            <p className="mt-2"><AutoTranslateText sourceLang="fr">Aucun bien disponible pour ces critères.</AutoTranslateText></p>
          </div>
        )}

        {/* ── Grille / Liste ── */}
        {filteredItems.length > 0 && (
          <div className={cfg.displayMode === 'grid' ? 'row g-4 pb-4' : 'd-flex flex-column gap-3 pb-4'}>
            {filteredItems.map(item => {
              const coverImg = item.images?.[0];
              const coverEmbed = coverImg ? getVideoEmbedUrl(coverImg) : null;
              const allAmenities = [...VACATION_AMENITIES, ...(cfg.customAmenities || [])];
              const badgeConf = getBadgeConf(item.badge, cfg.customBadges);
              const isSingle = filteredItems.length === 1;
              const topAmenities = (item.amenities || []).slice(0, isSingle ? 8 : 4).map(id => allAmenities.find(a => a.id === id)).filter(Boolean);
              const isGrid = cfg.displayMode === 'grid';

              return (
                <div key={item.id} className={isGrid ? (isSingle ? 'col-12 col-md-10 col-lg-8 mx-auto' : 'col-sm-6 col-lg-4') : ''}>
                  <div
                    className={`card border-0 shadow-sm vacation-card ${isGrid ? 'h-100' : 'd-flex flex-row'}`}
                    style={{ backgroundColor: cfg.itemBackgroundColor === 'transparent' ? 'transparent' : (cfg.itemBackgroundColor || '#fff'), borderRadius: 12, cursor: 'pointer', overflow: 'hidden' }}
                    onClick={e => openDetails(item, e)}
                  >
                    {/* Image */}
                    <div className={isGrid ? 'vacation-card-img position-relative' : 'flex-shrink-0 position-relative'} style={isGrid && isSingle ? { height: 320 } : isGrid ? {} : { width: 200, height: 150 }}>
                      {coverImg ? (
                        coverEmbed
                          ? <iframe src={coverEmbed} title={item.name} className="w-100 h-100 border-0" style={{ objectFit: 'cover' }} allow="accelerometer; autoplay; muted" />
                          : isVideoUrl(coverImg)
                          ? <video src={coverImg} className="w-100 h-100" style={{ objectFit: 'cover' }} autoPlay loop muted playsInline />
                          : <img src={coverImg} alt={item.name} className="w-100 h-100" style={{ objectFit: 'cover' }} />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: (cfg as any).cardBg || '#f8f9fa', minHeight: 150 }}>
                          <span style={{ fontSize: 40 }}>{CATEGORY_ICONS[item.category] || '🏠'}</span>
                        </div>
                      )}
                      {/* Badge */}
                      {badgeConf && (
                        <span className="position-absolute top-0 start-0 m-2 badge" style={{ backgroundColor: badgeConf.bg, color: badgeConf.color, fontSize: '0.7rem' }}>{badgeConf.label}</span>
                      )}
                      {/* Category chip */}
                      <span className="position-absolute bottom-0 end-0 m-2 badge rounded-pill" style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.7rem' }}>
                        {CATEGORY_ICONS[item.category]} {CATEGORY_LABELS[item.category] || item.category}
                      </span>
                    </div>

                    {/* Body */}
                    <div className="card-body d-flex flex-column p-3">
                      <h5 className={`card-title mb-1 ${isSingle ? '' : 'text-truncate'}`} style={{ fontSize: isSingle ? '1.25rem' : '1rem', fontWeight: 700 }}>{item.name}</h5>

                      {/* Catégorie (single) */}
                      {isSingle && (
                        <div className="text-muted small mb-2">{CATEGORY_ICONS[item.category]} {CATEGORY_LABELS[item.category] || item.category}</div>
                      )}

                      {/* Capacité */}
                      {item.capacity && (
                        <div className="d-flex gap-2 mb-2 text-muted" style={{ fontSize: '0.78rem' }}>
                          <span>👥 {item.capacity.guests}</span>
                          <span>🛏️ {item.capacity.bedrooms}</span>
                          <span>🛌 {item.capacity.beds}</span>
                          <span>🚿 {item.capacity.bathrooms}</span>
                        </div>
                      )}

                      {/* Description (single item) */}
                      {isSingle && item.description && (
                        <p className="text-muted small mb-2" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {(item.description || '').replace(/<[^>]+>/g, '').trim()}
                        </p>
                      )}

                      {/* Adresse (single item) */}
                      {isSingle && item.address && (
                        <div className="text-muted small mb-2">📍 {item.address}</div>
                      )}

                      {/* Équipements */}
                      {topAmenities.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {topAmenities.map(a => a && (
                            <span key={a.id} className="amenity-chip">{a.icon} {a.label}</span>
                          ))}
                        </div>
                      )}

                      {/* Prix */}
                      <div className="mt-auto d-flex justify-content-between align-items-center">
                        <div>
                          <span className="fw-bold" style={{ fontSize: isSingle ? '1.4rem' : '1.1rem', color: primaryColor }}>{item.pricePerDay} {cfg.currency}</span>
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}><AutoTranslateText sourceLang="fr"> / nuit</AutoTranslateText></span>
                          {item.weekendPricePerDay && item.weekendPricePerDay !== item.pricePerDay && (
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>{item.weekendPricePerDay} {cfg.currency}<AutoTranslateText sourceLang="fr"> / nuit (weekend)</AutoTranslateText></div>
                          )}
                        </div>
                        <button className={`btn fw-semibold ${isSingle ? '' : 'btn-sm'}`} style={{ backgroundColor: btnBg, color: btnTextColor, borderRadius: 8, padding: isSingle ? '8px 20px' : undefined }} onClick={e => { e.stopPropagation(); openDetails(item, e); }}>
                          {cfg.itemButtonText || 'Voir & Réserver'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination (si items > filteredItems, afficher compte) */}
        {cfg.items.length > filteredItems.length && (
          <div className="text-center text-muted small mb-3">
            {filteredItems.length} bien(s) sur {cfg.items.length} affiché(s) selon vos critères
          </div>
        )}
      </div>

      {/* ── MODAL DÉTAIL + CHECKOUT ── */}
      {activeItem && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }} role="dialog" aria-modal="true" onClick={e => { if (e.target === e.currentTarget) handleCloseModal(); }}>
          <div style={{ width: 'min(100%, 1100px)', maxHeight: 'calc(100vh - 24px)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ backgroundColor: '#ffffff', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 24px)' }}>

              {/* Header */}
              <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                <h5 className="mb-0 fw-bold text-truncate">
                  {showCheckout
                    ? (cfg.bookingMode === 'request' ? '📨 Demande de réservation' : '📝 Finaliser la réservation')
                    : activeItem.name}
                </h5>
                <button
                  className="btn btn-sm rounded-circle flex-shrink-0"
                  style={{ width: 32, height: 32, padding: 0, backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', lineHeight: 1, fontSize: '1rem' }}
                  onClick={handleCloseModal}
                  aria-label="Fermer"
                >✕</button>
              </div>

              <div style={{ overflowY: 'auto', flex: 1 }}>
                {!showCheckout && (
                  <>
                    {/* Galerie */}
                    {activeMediaItems.length > 0 && (
                      <div className="position-relative bg-black" style={{ height: 380 }}>
                        {(() => {
                          const media = activeMediaItems[modalMediaIdx];
                          const embedUrl = getVideoEmbedUrl(media);
                          return embedUrl
                            ? <iframe src={embedUrl} title="media" className="w-100 h-100 border-0" allow="autoplay; encrypted-media" allowFullScreen />
                            : isVideoUrl(media)
                            ? <video src={media} controls autoPlay loop muted playsInline className="w-100 h-100" style={{ objectFit: 'contain' }} />
                            : <img src={media} alt="media" className="d-block mx-auto h-100" style={{ objectFit: 'contain', maxWidth: '100%' }} />;
                        })()}
                        {activeMediaItems.length > 1 && (
                          <>
                            <button className="btn btn-dark position-absolute start-0 top-50 translate-middle-y ms-2 opacity-75" style={{ borderRadius: 8 }} onClick={() => setModalMediaIdx(i => (i - 1 + activeMediaItems.length) % activeMediaItems.length)}>‹</button>
                            <button className="btn btn-dark position-absolute end-0 top-50 translate-middle-y me-2 opacity-75" style={{ borderRadius: 8 }} onClick={() => setModalMediaIdx(i => (i + 1) % activeMediaItems.length)}>›</button>
                            <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-1 pb-2">
                              {activeMediaItems.map((_, i) => (
                                <span key={i} onClick={() => setModalMediaIdx(i)} style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: i === modalMediaIdx ? '#fff' : 'rgba(255,255,255,0.4)', cursor: 'pointer' }} />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="container-fluid p-4">
                      <div className="row g-4">
                        {/* Colonne infos */}
                        <div className="col-lg-6">
                          {/* Titre + badge */}
                          <div className="d-flex align-items-start gap-2 mb-2">
                            <div>
                              <h3 className="fw-bold mb-1">{activeItem.name}</h3>
                              <div className="text-muted small">{CATEGORY_ICONS[activeItem.category]} {CATEGORY_LABELS[activeItem.category] || activeItem.category}</div>
                            </div>
                            {activeItem.badge && (() => { const bc = getBadgeConf(activeItem.badge, cfg.customBadges); return bc ? <span className="badge ms-auto" style={{ backgroundColor: bc.bg, color: bc.color }}>{bc.label}</span> : null; })()}
                          </div>

                          {/* Capacité */}
                          {activeItem.capacity && (
                            <div className="d-flex gap-3 mb-3 p-2 rounded" style={{ background: (cfg as any).cardBg || '#f8f9fa' }}>
                              <div className="text-center"><div style={{ fontSize: 20 }}>👥</div><div className="small text-muted">{activeItem.capacity.guests} voyageurs</div></div>
                              <div className="text-center"><div style={{ fontSize: 20 }}>🛏️</div><div className="small text-muted">{activeItem.capacity.bedrooms} chambres</div></div>
                              <div className="text-center"><div style={{ fontSize: 20 }}>🛌</div><div className="small text-muted">{activeItem.capacity.beds} lits</div></div>
                              <div className="text-center"><div style={{ fontSize: 20 }}>🚿</div><div className="small text-muted">{activeItem.capacity.bathrooms} sdb</div></div>
                            </div>
                          )}

                          {/* Description */}
                          <div className="mb-3" dangerouslySetInnerHTML={{ __html: decodeHtml(activeItem.description || '') }} />

                          {/* Équipements */}
                          {(activeItem.amenities || []).length > 0 && (
                            <div className="mb-3">
                              <h6 className="fw-bold mb-2">Équipements</h6>
                              <div className="d-flex flex-wrap gap-2">
                                {(activeItem.amenities || []).map(id => {
                                  const allAmen = [...VACATION_AMENITIES, ...(cfg.customAmenities || [])];
                                  const a = allAmen.find(x => x.id === id);
                                  return a ? <span key={id} className="badge rounded-pill bg-light text-dark border" style={{ fontSize: '0.78rem' }}>{a.icon} {a.label}</span> : null;
                                })}
                              </div>
                            </div>
                          )}

                          {/* Legacy features */}
                          {activeItem.features && Object.keys(activeItem.features).length > 0 && (
                            <div className="mb-3">
                              <h6 className="fw-bold mb-2">Caractéristiques</h6>
                              {Object.entries(activeItem.features || {}).map(([k, v], i) => (
                                <div key={i} className="d-flex justify-content-between border-bottom pb-1 mb-1"><span className="text-muted small">{k}</span><span className="fw-semibold small">{v}</span></div>
                              ))}
                            </div>
                          )}

                          {/* Règles */}
                          {activeItem.rules && (
                            <div className="mb-3">
                              <h6 className="fw-bold mb-2">Règles du séjour</h6>
                              <div className="d-flex flex-wrap gap-2 small text-muted">
                                {activeItem.rules.checkInTime && <span>🏠 Arrivée dès {activeItem.rules.checkInTime}</span>}
                                {activeItem.rules.checkOutTime && <span>🚪 Départ avant {activeItem.rules.checkOutTime}</span>}
                                {activeItem.rules.minNights && <span>📅 Min. {activeItem.rules.minNights} nuits</span>}
                                {activeItem.rules.maxNights && <span>📅 Max. {activeItem.rules.maxNights} nuits</span>}
                                <span>{activeItem.rules.smokingAllowed ? '🚬 Fumeurs OK' : '🚭 Non fumeur'}</span>
                                <span>{activeItem.rules.petsAllowed ? '🐾 Animaux OK' : '🚫 Animaux refusés'}</span>
                                <span>{activeItem.rules.eventsAllowed ? '🎉 Événements OK' : '🚫 Fêtes interdites'}</span>
                              </div>
                            </div>
                          )}

                          {/* Google Maps */}
                          {activeItem.address && (
                            <div className="mb-3">
                              <div id={`modal-map-${activeItem.id}`} style={{ height: 180, width: '100%', borderRadius: 8, border: '1px solid #e2e8f0', backgroundColor: '#f8f9fa' }}></div>
                              <div className="text-muted small mt-1">📍 {activeItem.address}</div>
                            </div>
                          )}
                        </div>

                        {/* Colonne réservation */}
                        <div className="col-lg-6">
                          <div className="card bg-light border-0 shadow-sm p-3 sticky-top" style={{ top: 8 }}>
                            <h5 className="fw-bold text-center mb-3"><AutoTranslateText sourceLang="fr">Réservation</AutoTranslateText></h5>

                            {/* Prix principal */}
                            <div className="text-center mb-3">
                              <span className="fw-bold" style={{ fontSize: '1.5rem', color: primaryColor }}>{activeItem.pricePerDay} {cfg.currency}</span>
                              <span className="text-muted"> / nuit</span>
                              {activeItem.weekendPricePerDay && activeItem.weekendPricePerDay !== activeItem.pricePerDay && (
                                <div className="text-muted small">{activeItem.weekendPricePerDay} {cfg.currency} / nuit (ven-sam)</div>
                              )}
                              {(activeItem.seasonalPrices || []).length > 0 && (
                                <div className="text-muted small">🌡️ Tarifs saisonniers disponibles</div>
                              )}
                            </div>

                            {/* DateRangePicker interactif */}
                            <DateRangePicker
                              bookings={getMergedBookings(activeItem)}
                              startDate={startDate}
                              endDate={endDate}
                              onStartChange={handleStartDateChange}
                              onEndChange={handleEndDateChange}
                              primaryColor={primaryColor}
                              minNights={activeItem.rules?.minNights}
                              maxNights={activeItem.rules?.maxNights}
                            />
                            {startDateError && <div className="text-danger small mt-1">{startDateError}</div>}
                            {endDateError && <div className="text-danger small mt-1">{endDateError}</div>}

                            {/* Voyageurs */}
                            <div className="mt-2">
                              <label className="form-label small mb-1">👥 <AutoTranslateText sourceLang="fr">Nombre de voyageurs</AutoTranslateText></label>
                              <input type="number" className="form-control" min={1} max={activeItem.capacity?.guests || 20} value={guestsCount} onChange={e => setGuestsCount(parseInt(e.target.value) || 1)} />
                            </div>

                            {/* Décompte prix */}
                            {priceBreakdown && priceBreakdown.nights > 0 && (
                              <div className="mt-3 border-top pt-3">
                                <div className="d-flex justify-content-between small text-muted mb-1">
                                  <span>{priceBreakdown.nights} nuits</span>
                                  <span>{priceBreakdown.nightsTotal} {cfg.currency}</span>
                                </div>
                                {priceBreakdown.cleaningFee > 0 && (
                                  <div className="d-flex justify-content-between small text-muted mb-1">
                                    <span>🧹 Frais de ménage</span>
                                    <span>{priceBreakdown.cleaningFee} {cfg.currency}</span>
                                  </div>
                                )}
                                {priceBreakdown.touristTax > 0 && (
                                  <div className="d-flex justify-content-between small text-muted mb-1">
                                    <span>🏛️ Taxe de séjour ({guestsCount} pers. × {priceBreakdown.nights} nuits)</span>
                                    <span>{priceBreakdown.touristTax} {cfg.currency}</span>
                                  </div>
                                )}
                                {(cfg.depositAmount || 0) > 0 && (
                                  <div className="d-flex justify-content-between small text-info mb-1">
                                    <span>🔒 Caution (remboursée sous {cfg.depositRefundDays || 14}j)</span>
                                    <span>{cfg.depositAmount} {cfg.currency}</span>
                                  </div>
                                )}
                                <div className="d-flex justify-content-between fw-bold mt-2 pt-2 border-top">
                                  <span><AutoTranslateText sourceLang="fr">Total</AutoTranslateText></span>
                                  <span style={{ color: primaryColor, fontSize: '1.2rem' }}>{totalPrice} {cfg.currency}</span>
                                </div>
                              </div>
                            )}

                            {/* Mode réservation badge */}
                            {cfg.bookingMode === 'request' && (
                              <div className="alert alert-warning py-2 small mt-2 mb-0">
                                📨 <strong>Sur demande</strong> — le propriétaire confirmera votre réservation sous 24h.
                              </div>
                            )}

                            <button
                              className="btn w-100 py-2 fw-bold mt-3"
                              style={{ backgroundColor: primaryColor, color: '#fff', borderRadius: 8 }}
                              disabled={totalPrice <= 0}
                              onClick={handleProceedToCheckout}
                            >
                              {cfg.bookingMode === 'request' ? '📨 Envoyer une demande' : '✅ Confirmer la réservation'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ── CHECKOUT ── */}
                {showCheckout && (
                  <div className="container-fluid p-4">
                    <div className="rental-checkout-grid">
                      {/* Infos client */}
                      <div>
                        <h6 className="text-primary mb-3"><AutoTranslateText sourceLang="fr">Vos informations</AutoTranslateText></h6>
                        <div className="alert alert-info py-2 small mb-3">Tous les champs sont obligatoires.</div>
                        <div className="row g-2 mb-2">
                          <div className="col-6">
                            <div className="form-floating">
                              <input type="text" className={`form-control ${!customerInfo.lastName ? 'border-warning' : ''}`} placeholder="Nom" value={customerInfo.lastName} onChange={e => setCustomerInfo({ ...customerInfo, lastName: e.target.value })} required />
                              <label><AutoTranslateText sourceLang="fr">Nom *</AutoTranslateText></label>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="form-floating">
                              <input type="text" className={`form-control ${!customerInfo.firstName ? 'border-warning' : ''}`} placeholder="Prénom" value={customerInfo.firstName} onChange={e => setCustomerInfo({ ...customerInfo, firstName: e.target.value })} required />
                              <label><AutoTranslateText sourceLang="fr">Prénom *</AutoTranslateText></label>
                            </div>
                          </div>
                        </div>
                        <div className="form-floating mb-2">
                          <input type="email" className={`form-control ${!customerInfo.email ? 'border-warning' : ''}`} placeholder="Email" value={customerInfo.email} onChange={e => setCustomerInfo({ ...customerInfo, email: e.target.value })} required />
                          <label><AutoTranslateText sourceLang="fr">Email *</AutoTranslateText></label>
                        </div>
                        <div className="form-floating mb-2">
                          <input type="tel" className={`form-control ${!customerInfo.phone ? 'border-warning' : ''}`} placeholder="Téléphone" value={customerInfo.phone} onChange={e => setCustomerInfo({ ...customerInfo, phone: e.target.value })} required />
                          <label><AutoTranslateText sourceLang="fr">Téléphone *</AutoTranslateText></label>
                        </div>
                        <div className="form-floating mb-2">
                          <input type="text" className={`form-control ${!customerInfo.address ? 'border-warning' : ''}`} placeholder="Adresse" value={customerInfo.address} onChange={e => setCustomerInfo({ ...customerInfo, address: e.target.value })} required />
                          <label><AutoTranslateText sourceLang="fr">Adresse *</AutoTranslateText></label>
                        </div>
                        <div className="row g-2">
                          <div className="col-4">
                            <div className="form-floating">
                              <input type="text" className={`form-control ${!customerInfo.postalCode ? 'border-warning' : ''}`} placeholder="CP" value={customerInfo.postalCode} onChange={e => setCustomerInfo({ ...customerInfo, postalCode: e.target.value })} required />
                              <label>CP *</label>
                            </div>
                          </div>
                          <div className="col-8">
                            <div className="form-floating">
                              <input type="text" className={`form-control ${!customerInfo.city ? 'border-warning' : ''}`} placeholder="Ville" value={customerInfo.city} onChange={e => setCustomerInfo({ ...customerInfo, city: e.target.value })} required />
                              <label><AutoTranslateText sourceLang="fr">Ville *</AutoTranslateText></label>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <select className="form-select" value={customerInfo.country} onChange={e => setCustomerInfo({ ...customerInfo, country: e.target.value })}>
                            {ALL_COUNTRIES.map(c => <option key={c.code} value={c.label}>{c.label}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Récap + paiement */}
                      <div>
                        <div className="bg-light p-3 rounded mb-3">
                          <h6 className="text-muted text-uppercase small fw-bold mb-2"><AutoTranslateText sourceLang="fr">Récapitulatif</AutoTranslateText></h6>
                          <div className="d-flex justify-content-between mb-1"><span>{activeItem.name}</span></div>
                          <div className="d-flex justify-content-between mb-1 small text-muted"><span><AutoTranslateText sourceLang="fr">Dates</AutoTranslateText></span><span>{new Date(startDate).toLocaleDateString()} → {new Date(endDate).toLocaleDateString()}</span></div>
                          <div className="d-flex justify-content-between mb-1 small text-muted"><span>👥 Voyageurs</span><span>{guestsCount}</span></div>
                          {priceBreakdown && (
                            <>
                              <div className="d-flex justify-content-between mb-1 small text-muted"><span>{priceBreakdown.nights} nuits</span><span>{priceBreakdown.nightsTotal} {cfg.currency}</span></div>
                              {priceBreakdown.cleaningFee > 0 && <div className="d-flex justify-content-between mb-1 small text-muted"><span>🧹 Ménage</span><span>{priceBreakdown.cleaningFee} {cfg.currency}</span></div>}
                              {priceBreakdown.touristTax > 0 && <div className="d-flex justify-content-between mb-1 small text-muted"><span>🏛️ Taxe séjour</span><span>{priceBreakdown.touristTax} {cfg.currency}</span></div>}
                            </>
                          )}
                          {(cfg.depositAmount || 0) > 0 && <div className="d-flex justify-content-between mb-1 small text-info"><span>🔒 Caution (remboursée)</span><span>{cfg.depositAmount} {cfg.currency}</span></div>}
                          <div className="d-flex justify-content-between border-top pt-2 mt-2 fw-bold"><span><AutoTranslateText sourceLang="fr">Total à régler</AutoTranslateText></span><span className="text-success fs-5">{totalPrice} {cfg.currency}</span></div>
                        </div>

                        <h6 className="text-primary mb-3"><AutoTranslateText sourceLang="fr">Paiement</AutoTranslateText></h6>

                        {paymentStatus === 'error' && paymentMessage && (
                          <div className="alert alert-danger py-2 small">{paymentMessage}</div>
                        )}
                        {paymentStatus === 'success' && (
                          <div className="alert alert-success d-flex align-items-center">
                            <span style={{ fontSize: 28 }} className="me-3">✅</span>
                            <div><strong>{cfg.bookingMode === 'request' ? 'Demande envoyée !' : 'Paiement réussi !'}</strong><br />{paymentMessage}</div>
                          </div>
                        )}
                        {paymentStatus === 'cancelled' && paymentMessage && (
                          <div className="alert alert-warning py-2 small">{paymentMessage}</div>
                        )}

                        {paymentStatus !== 'success' && (
                          <>
                            <div className="btn-group w-100 mb-3" role="group">
                              {merchantKeys.stripePublicKey && <><input type="radio" className="btn-check" name="payment" id="pay-stripe" checked={paymentMethod === 'stripe'} onChange={() => setPaymentMethod('stripe')} /><label className="btn btn-outline-primary" htmlFor="pay-stripe">💳 Stripe</label></>}
                              {merchantKeys.paypalClientId && <><input type="radio" className="btn-check" name="payment" id="pay-paypal" checked={paymentMethod === 'paypal'} onChange={() => setPaymentMethod('paypal')} /><label className="btn btn-outline-primary" htmlFor="pay-paypal">🅿️ PayPal</label></>}
                              <input type="radio" className="btn-check" name="payment" id="pay-manual" checked={paymentMethod === 'manual'} onChange={() => setPaymentMethod('manual')} />
                              <label className="btn btn-outline-primary" htmlFor="pay-manual">🏦 Virement / Sur place</label>
                            </div>

                            {isProcessing ? (
                              <div className="text-center py-4"><div className="spinner-border text-primary"></div><p className="mt-2 text-muted"><AutoTranslateText sourceLang="fr">Traitement...</AutoTranslateText></p></div>
                            ) : (
                              <>
                                {paymentMethod === 'stripe' && (
                                  <div className="text-center">
                                    {!stripeClientSecret && <button className="btn btn-primary w-100 fw-bold" onClick={createStripeIntent} disabled={stripeBusy}>{stripeBusy ? 'Préparation...' : 'Continuer avec Stripe'}</button>}
                                    {stripeClientSecret && stripePromise && stripeElementsOptions && (
                                      <Elements stripe={stripePromise} options={stripeElementsOptions}>
                                        <StripePaymentElementForm onSuccess={handleStripePaymentSuccess} onError={handlePaymentError} submitLabel="Payer par carte" disabled={isProcessing} />
                                      </Elements>
                                    )}
                                  </div>
                                )}
                                {paymentMethod === 'paypal' && merchantKeys.paypalClientId && (
                                  <PayPalScriptProvider options={{ clientId: merchantKeys.paypalClientId.split(':')[0].trim(), currency: cfg.currency?.length === 3 ? cfg.currency : 'EUR', intent: "capture" }}>
                                    <PayPalCheckoutButtons amount={totalPrice.toFixed(2)} currency={cfg.currency} onSuccess={d => handlePaymentSuccess({ ...d, method: 'paypal' })} onError={handlePaymentError} onCancel={handlePaymentCancel} />
                                  </PayPalScriptProvider>
                                )}
                                {paymentMethod === 'manual' && (
                                  <div className="text-center">
                                    <p className="small text-muted mb-3"><AutoTranslateText sourceLang="fr">La réservation sera mise en attente de validation.</AutoTranslateText></p>
                                    <button className="btn btn-success w-100 fw-bold" onClick={() => finalizeBooking('manual')}>{cfg.bookingMode === 'request' ? '📨 Envoyer la demande' : '✅ Confirmer la réservation'}</button>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}

                        <button className="btn btn-link text-muted w-100 mt-3" onClick={() => setShowCheckout(false)} disabled={isProcessing && paymentStatus !== 'success'}>
                          {paymentStatus === 'success' ? 'Fermer' : '← Retour'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
